import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Copy, Trash2, UserPlus, Users, X } from "lucide-react";
import { secretClipboard } from "../../app/clipboard.js";
import { inviteFragment } from "../../lib/invite.js";
import { useToast } from "../../ui/Toast.js";
import { Field, SelectInput, TextInput } from "../../ui/forms.js";
import { Alert, Avatar, Badge, Button, Card, CardHeader, IconButton, Skeleton } from "../../ui/primitives.js";
import type { InviteView, OrgDetail, OrgMemberView, TeamRole } from "../../api/teams.js";
import {
  useChangeRole,
  useCreateInvite,
  useInvites,
  useOwnMembership,
  useRemoveMember,
  useRevokeInvite
} from "../../data/teams.js";
import type { InviteCreated } from "../../data/teams.js";
import { formatRelativeTime } from "../../lib/format.js";
import { ROLE_DESCRIPTION, ROLE_LABEL, assignableRoles, canManageTeam, describeTeamError, isOwnerRole } from "../../lib/teams.js";
import { paths } from "../../app/paths.js";

interface MembersTabProps {
  readonly org: OrgDetail;
  readonly members: ReadonlyArray<OrgMemberView> | undefined;
  readonly membersPending: boolean;
  readonly membersError: boolean;
}

export function TeamMembersTab(props: MembersTabProps): ReactNode {
  const { org } = props;
  const manage = canManageTeam(org.role);
  const self = useOwnMembership(props.members);

  return (
    <div className="flex flex-col gap-6">
      <Card className="overflow-hidden">
        <CardHeader
          title="Mitglieder"
          description="Rollen bestimmen, wer lesen, schreiben und verwalten darf."
          icon={Users}
          action={props.members !== undefined ? <Badge tone="neutral">{props.members.length}</Badge> : undefined}
        />
        {props.membersPending ? (
          <div className="p-5">
            <Skeleton className="h-32" />
          </div>
        ) : null}
        {props.membersError ? (
          <div className="p-5">
            <Alert tone="danger">Mitglieder konnten nicht geladen werden.</Alert>
          </div>
        ) : null}
        <ul className="divide-y divide-line">
          {(props.members ?? []).map((member: OrgMemberView) => (
            <MemberRow key={member.userId} org={org} member={member} isSelf={self?.userId === member.userId} manage={manage} />
          ))}
        </ul>
      </Card>

      {manage ? <InvitePanel org={org} /> : null}
    </div>
  );
}

function MemberRow(props: { readonly org: OrgDetail; readonly member: OrgMemberView; readonly isSelf: boolean; readonly manage: boolean }): ReactNode {
  const { org, member } = props;
  const toast = useToast();
  const changeRole = useChangeRole(org.id);
  const remove = useRemoveMember(org.id);
  const [confirming, setConfirming] = useState(false);
  const actorIsOwner = isOwnerRole(org.role);
  const editable = props.manage && !props.isSelf && (actorIsOwner || member.role !== "owner");
  const options = [...new Set<TeamRole>([...assignableRoles(org.role), member.role])];
  const removable = editable;

  function onRemove(): void {
    remove.mutate(member.userId, {
      onSuccess: (result): void => {
        setConfirming(false);
        if (result.rotated) {
          toast.notify("Mitglied entfernt. Der Team-Schlüssel wurde rotiert.", { tone: "success" });
        } else {
          toast.notify("Mitglied entfernt. Die Schlüssel-Rotation ist fehlgeschlagen. Bitte unter Einstellungen erneut ausführen.", { tone: "danger" });
        }
      },
      onError: (err: Error): void => {
        toast.notify(describeTeamError(err), { tone: "danger" });
        setConfirming(false);
      }
    });
  }

  return (
    <li className="flex flex-col gap-3 px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar label={member.email ?? "?"} />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foam">
            {member.email ?? "Unbekanntes Konto"}
            {props.isSelf ? <span className="ml-2 rounded border border-line px-1.5 py-px text-[0.68rem] font-normal text-mist-2">Du</span> : null}
          </p>
          <p className="truncate text-[0.8125rem] text-mist-2">{ROLE_DESCRIPTION[member.role]}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:shrink-0">
        {editable ? (
          <div className="w-36">
            <SelectInput
              aria-label={`Rolle von ${member.email ?? "Mitglied"}`}
              value={member.role}
              disabled={changeRole.isPending}
              onChange={(event): void => {
                const role = options.find((o: TeamRole): boolean => o === event.target.value);
                if (role !== undefined && role !== member.role) {
                  changeRole.mutate(
                    { userId: member.userId, role },
                    {
                      onError: (err: Error): void => toast.notify(describeTeamError(err), { tone: "danger" })
                    }
                  );
                }
              }}
            >
              {options.map((role: TeamRole) => (
                <option key={role} value={role}>
                  {ROLE_LABEL[role]}
                </option>
              ))}
            </SelectInput>
          </div>
        ) : (
          <Badge tone={member.role === "owner" ? "mint" : "neutral"}>{ROLE_LABEL[member.role]}</Badge>
        )}
        {removable && !confirming ? (
          <IconButton label={`${member.email ?? "Mitglied"} entfernen`} icon={Trash2} tone="danger" size="sm" onClick={(): void => setConfirming(true)} />
        ) : null}
        {removable && confirming ? (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={(): void => setConfirming(false)}>
              Abbrechen
            </Button>
            <Button variant="danger" size="sm" loading={remove.isPending} onClick={onRemove}>
              Entfernen
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function InvitePanel(props: { readonly org: OrgDetail }): ReactNode {
  const { org } = props;
  const invites = useInvites(org.id, true);
  const create = useCreateInvite(org);
  const revoke = useRevokeInvite(org.id);
  const toast = useToast();
  const roles = assignableRoles(org.role);
  const [email, setEmail] = useState("");
  // Least surprise: invite as plain member unless the inviter deliberately picks more.
  const [role, setRole] = useState<TeamRole>(roles.includes("member") ? "member" : roles[0] ?? "viewer");
  const [error, setError] = useState<string | undefined>(undefined);
  const [created, setCreated] = useState<InviteCreated | undefined>(undefined);
  const pending = (invites.data ?? []).filter((i: InviteView): boolean => i.acceptedAt === null && i.expiresAt > Date.now());

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(trimmed)) {
      setError("Bitte gib eine gültige E-Mail-Adresse ein.");
      return;
    }
    create.mutate(
      { email: trimmed, role },
      {
        onSuccess: (result: InviteCreated): void => {
          setCreated(result);
          setEmail("");
          setError(undefined);
        },
        onError: (err: Error): void => setError(describeTeamError(err))
      }
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader title="Mitglied einladen" description="Du erhältst einen Link. Er enthält das Geheimnis zum Beitreten und wird nur einmal angezeigt." icon={UserPlus} />
      <div className="flex flex-col gap-5 p-5">
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_11rem_auto] sm:items-end" noValidate>
          <Field id="invite-email" label="E-Mail-Adresse" error={error}>
            <TextInput
              id="invite-email"
              type="email"
              value={email}
              autoComplete="off"
              placeholder="name@firma.de"
              onChange={(event): void => {
                setEmail(event.target.value);
                setError(undefined);
              }}
            />
          </Field>
          <Field id="invite-role" label="Rolle">
            <SelectInput
              id="invite-role"
              value={role}
              onChange={(event): void => {
                const picked = roles.find((r: TeamRole): boolean => r === event.target.value);
                if (picked !== undefined) {
                  setRole(picked);
                }
              }}
            >
              {roles.map((r: TeamRole) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Button type="submit" icon={UserPlus} loading={create.isPending}>
            Einladen
          </Button>
        </form>

        {created !== undefined ? <InviteLink invite={created} onDismiss={(): void => setCreated(undefined)} /> : null}

        <div>
          <p className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-mist-2">Offene Einladungen</p>
          {pending.length === 0 ? (
            <p className="mt-2 text-sm text-mist">Keine offenen Einladungen.</p>
          ) : (
            <ul className="mt-2 divide-y divide-line rounded-lg border border-line bg-ink">
              {pending.map((invite: InviteView) => (
                <li key={invite.id} className="flex items-center justify-between gap-4 px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 truncate text-sm text-foam">
                      {invite.email}
                      {invite.stale === true ? <Badge tone="buoy">Ungültig</Badge> : null}
                    </p>
                    <p className="text-xs text-mist-2">
                      {invite.stale === true
                        ? "Team-Schlüssel wurde seitdem erneuert. Widerrufen und neu einladen."
                        : `${ROLE_LABEL[invite.role]} · läuft ${formatRelativeTime(invite.expiresAt, Date.now())} ab`}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={revoke.isPending}
                    onClick={(): void => {
                      revoke.mutate(invite.id, {
                        onError: (err: Error): void => toast.notify(describeTeamError(err), { tone: "danger" })
                      });
                    }}
                  >
                    Widerrufen
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

function InviteLink(props: { readonly invite: InviteCreated; readonly onDismiss: () => void }): ReactNode {
  const toast = useToast();
  const link = `${window.location.origin}${paths.invite}${inviteFragment(props.invite.secretB64)}`;

  async function copy(): Promise<void> {
    const result = await secretClipboard.copy(link);
    if (result === "copied") {
      toast.notify(`Einladungslink kopiert. Die Zwischenablage wird in ${Math.round(secretClipboard.clearAfterMs / 1000)} Sekunden geleert.`, { tone: "success" });
    } else {
      toast.notify("Kopieren fehlgeschlagen. Markiere den Link und kopiere ihn manuell.", { tone: "danger" });
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-flow/25 bg-flow/[0.06] p-4">
      <p className="text-sm text-foam">
        Link für <span className="font-medium">{props.invite.email}</span> ({ROLE_LABEL[props.invite.role]}). Gib ihn auf sicherem Weg weiter. Er ist 7 Tage gültig und funktioniert nur mit dem eingeladenen Konto.
      </p>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md border border-line bg-ink px-3 py-2 font-mono text-xs text-mist">{link}</code>
        <Button variant="secondary" size="sm" icon={Copy} onClick={(): void => void copy()}>
          Kopieren
        </Button>
        <IconButton label="Hinweis schließen" icon={X} size="sm" onClick={props.onDismiss} />
      </div>
    </div>
  );
}
